import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Skeleton } from '@/components/ui/skeleton'

import { formatRemainingTimeUnit, getRemainingParkingTime } from '../visitor-parking.utils'
import * as S from './parking-time-counter.styled'

const TIMER_INTERVAL = 1000 // 1 second

interface ParkingTimeCounterProps {
  endDate?: Date | number
  loading?: boolean
}

export const ParkingTimeCounter = ({ endDate, loading }: ParkingTimeCounterProps) => {
  const { t } = useTranslation(['visitor-parking'])
  const [timeRemaining, setTimeRemaining] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
  })

  useEffect(() => {
    if (!endDate || loading) return

    setTimeRemaining(getRemainingParkingTime(endDate))

    const interval = setInterval(() => {
      setTimeRemaining(getRemainingParkingTime(endDate))
    }, TIMER_INTERVAL)

    return () => clearInterval(interval)
  }, [endDate, loading])

  if (loading) {
    return (
      <S.TimerContainer>
        <Skeleton width="22rem" height="6rem" />
        <Skeleton width="16rem" height="2.1rem" />
      </S.TimerContainer>
    )
  }

  if (!endDate) return null

  const { hours, minutes, seconds } = timeRemaining
  const isExpired = hours === 0 && minutes === 0 && seconds === 0

  return (
    <S.TimerContainer>
      <S.TimeLeft>
        {hours === 0 ? (
          <S.TimeLeftDimmed>{formatRemainingTimeUnit(hours)}:</S.TimeLeftDimmed>
        ) : (
          <span>{formatRemainingTimeUnit(hours)}:</span>
        )}
        {hours === 0 && minutes === 0 ? (
          <S.TimeLeftDimmed>{formatRemainingTimeUnit(minutes)}:</S.TimeLeftDimmed>
        ) : (
          <span>{formatRemainingTimeUnit(minutes)}:</span>
        )}
        {isExpired ? (
          <S.TimeLeftDimmed>{formatRemainingTimeUnit(seconds)}</S.TimeLeftDimmed>
        ) : (
          <span>{formatRemainingTimeUnit(seconds)}</span>
        )}
      </S.TimeLeft>
      <S.TimeLeftDescription>
        {isExpired
          ? t('visitor-parking:parkingTimeCounter.expired')
          : t('visitor-parking:parkingTimeCounter.remaining')}
      </S.TimeLeftDescription>
    </S.TimerContainer>
  )
}
